/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import MaterialSymbol from '../components/MaterialSymbol';
import { Network } from '@capacitor/network';
import { Capacitor } from '@capacitor/core';

const OnlineStatusContext = React.createContext(true);

type OnlineStatusProps = {
  children?: React.ReactNode;
};

export const OnlineStatusProvider: React.FC<OnlineStatusProps> = ({ children }) => {
  const [onlineStatus, setOnlineStatus] = useState<boolean>(true);
  const [nativeOnline, setNativeOnline] = React.useState(true);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      Network.addListener('networkStatusChange', (status) => {
        console.log('Network status changed', status);
        setNativeOnline(status.connected);
      });
      (async () => {
        const status = await Network.getStatus();
        setNativeOnline(status.connected);
      })();

      return () => {
        Network.removeAllListeners();
      };
    }
  }, []);

  const offline = React.useCallback(() => {
    if (onlineStatus === true) {
      toast.dismiss('Online-toast-id');
      setTimeout(() => {
        toast('You are offline', {
          toastId: 'Offline-toast-id',
          autoClose: false,
          type: 'warning',
          icon: <MaterialSymbol>wifi_off</MaterialSymbol>,
        });
      }, 1000);
    }
    setOnlineStatus(false);
  }, [onlineStatus]);

  const online = React.useCallback(() => {
    if (onlineStatus === false) {
      toast.dismiss('Offline-toast-id');
      setTimeout(() => {
        toast('You are back online', {
          toastId: 'Online-toast-id',
          autoClose: false,
          type: 'success',
          icon: <MaterialSymbol>wifi</MaterialSymbol>,
        });
      }, 1000);
    }
    setOnlineStatus(true);
  }, [onlineStatus]);

  useEffect(() => {
    window.addEventListener('offline', offline);
    window.addEventListener('online', online);

    return () => {
      window.removeEventListener('offline', offline);
      window.removeEventListener('online', online);
    };
  }, [online, offline]);

  return (
    <OnlineStatusContext.Provider
      value={Capacitor.isNativePlatform() ? nativeOnline : onlineStatus}
    >
      {children}
    </OnlineStatusContext.Provider>
  );
};

export const useConnectionEffect = (callback: (online: boolean) => void) => {
  const online = useContext(OnlineStatusContext);

  React.useEffect(() => {
    callback(online);
  }, [online]);
};

export const useOnlineStatus = () => {
  const online = useContext(OnlineStatusContext);

  return online;
};
